import type {DiffEntry} from '@github-ui/diff-lines'
import {produce} from 'immer'
import type {StateCreator} from 'zustand'
import {loadDiffEntriesForPaths} from '../../page-data/loaders/use-diff-entries'

export interface DiffEntriesSliceOpts {
  diffEntries: Record<string, DiffEntry>
}

export type DiffEntriesSlice = {
  diffEntries: Record<string, DiffEntry>

  diffEntriesActions: {
    updateDiffEntries: (diffEntries: DiffEntry[]) => void
    loadDiffEntries: (variables: {
      paths: string[]
      signal?: AbortSignal
      basePath: string
      selectedRange: {
        headOid: string
        baseOid: string
      }
      fullDiff: {
        headOid: string
        baseOid: string
      }
      hideWhitespace: boolean
    }) => Promise<DiffEntry[]>
  }
}

export const createDiffEntriesSlice =
  ({
    diffEntries,
  }: DiffEntriesSliceOpts): StateCreator<DiffEntriesSlice, [['zustand/devtools', never]], [], DiffEntriesSlice> =>
  (set, get) => ({
    diffEntries,

    diffEntriesActions: {
      updateDiffEntries: updatedDiffEntries => {
        const oldDiffEntryMap = get().diffEntries
        if (!oldDiffEntryMap) {
          return
        }

        set(
          {
            diffEntries: produce(oldDiffEntryMap, (m: Record<string, DiffEntry>) => {
              for (const diffEntry of updatedDiffEntries) {
                m[diffEntry.path] = diffEntry
              }
            }),
          },
          undefined,
          'updateDiffEntriesInMap',
        )
      },
      loadDiffEntries: async (variables: {
        paths: string[]
        signal?: AbortSignal
        basePath: string
        selectedRange: {
          headOid: string
          baseOid: string
        }
        fullDiff: {
          headOid: string
          baseOid: string
        }
        hideWhitespace: boolean
      }) => {
        const loadedEntries = await loadDiffEntriesForPaths(variables)
        if (!loadedEntries || loadedEntries.length === 0) {
          return []
        }
        get().diffEntriesActions.updateDiffEntries(loadedEntries)

        return loadedEntries
      },
    },
  })
